import { type Tree, type TreeCursor } from '@lezer/common';
import { type LRParser } from '@lezer/lr';

import { formulaParser } from '../lezer-syntax-parser';

export interface CodeToken {
  name: string;
  text: string;
  from: number;
  to: number;
  children: CodeToken[];
}

const traverseCodeTokens = (cursor: TreeCursor, code: string): CodeToken => {
  const node = {
    name: cursor.type.name,
    from: cursor.from,
    to: cursor.to,
    text: code.slice(cursor.from, cursor.to),
    children: [] as CodeToken[],
  };

  if (cursor.firstChild()) {
    do {
      node.children.push(traverseCodeTokens(cursor, code));
    } while (cursor.nextSibling());
    cursor.parent();
  }
  return node;
};

export const checkCodeHasErrors = (cursor: TreeCursor, code: string): boolean => {
  const codeTree = traverseCodeTokens(cursor, code);
  return getAllTokens(codeTree).some((node) => node.name === '⚠');
};

export const buildTokensTree = (
  code: string,
  customParser: LRParser = formulaParser,
): CodeToken => {
  const tree = customParser.parse(code);
  return traverseCodeTokens(tree.cursor(), code);
};

export const getAllTokens = (node: CodeToken, acc: CodeToken[] = []): CodeToken[] => {
  acc.push(node);
  node.children.forEach((child) => getAllTokens(child, acc));
  return acc;
};

export const getTokenByPath = (tree: CodeToken, path: string[]): CodeToken[] => {
  if (path.length === 0) {
    return [tree];
  }
  const [currentPath, ...rest] = path;
  return tree.children.flatMap((child) => {
    if (child.name === currentPath || currentPath === '*') {
      return getTokenByPath(child, rest);
    }
    return [];
  });
};

export const getAllTokensByName = (tree: Tree, code: string, name: string): CodeToken[] => {
  const cursor = tree.cursor();
  const root: CodeToken = traverseCodeTokens(cursor, code);
  return traverseForToken(root, name);
};

const traverseForToken = (node: CodeToken, name: string, acc: CodeToken[] = []): CodeToken[] => {
  if (node.name === name || name === '*') {
    acc.push(node);
  }
  node.children.forEach((child) => traverseForToken(child, name, acc));
  return acc;
};
